import { useMemo } from 'react';
import {
  FormTreeData,
  QuestionData,
  SectionStatus,
  FormFieldValue,
} from '@/features/submission_forms/types';
import { useClarificationsContext } from '@/contexts/ClarificationsContext';
import { Clarification } from '@/types/clarification';
import { useSubmissionFormContext } from '@/features/submission_forms/SubmissionFormContext';
import {
  isAnsweredValue,
  isQuestionVisibleTransitive,
} from './questionVisibility';

interface SubsectionStatusResult {
  status: SectionStatus;
  hasClarifications: boolean;
  unresolvedCount: number;
  totalClarificationCount: number;
}

const collectQuestions = (node: FormTreeData): QuestionData[] => {
  const questions: QuestionData[] = [...node.questions];
  node.children.forEach(child => {
    questions.push(...collectQuestions(child));
  });
  return questions;
};

/**
 * Computes form-completion and clarification status for a FormTreeData node.
 * Accepts formValues from the caller to avoid a duplicate useWatch subscription.
 */
export const useSubsectionStatus = (
  node: FormTreeData,
  formValues: Record<string, FormFieldValue>
): SubsectionStatusResult => {
  const { clarificationsByQuestion } = useClarificationsContext();
  // Full-form slug map: ancestors may live in another group, and visibility
  // must be transitive to agree with the backend ProgressService
  const { questionsBySlug: allQuestionsBySlug } = useSubmissionFormContext();

  const questions = useMemo(() => collectQuestions(node), [node]);

  // Fall back to this node's own questions when no provider is present
  const questionsBySlug = useMemo(
    () =>
      allQuestionsBySlug.size > 0
        ? allQuestionsBySlug
        : new Map(questions.map(q => [q.slug, q])),
    [allQuestionsBySlug, questions]
  );

  const status: SectionStatus = useMemo(() => {
    const requiredVisible = questions.filter(
      q =>
        q.required &&
        isQuestionVisibleTransitive(q, formValues, questionsBySlug)
    );

    if (requiredVisible.length === 0) {
      const anyAnswered = questions.some(q =>
        isAnsweredValue(formValues[q.slug])
      );
      return anyAnswered ? 'complete' : 'optional';
    }

    const answeredCount = requiredVisible.filter(q =>
      isAnsweredValue(formValues[q.slug])
    ).length;

    if (answeredCount === requiredVisible.length) return 'complete';
    if (answeredCount > 0) return 'in_progress';
    return 'not_started';
  }, [questions, questionsBySlug, formValues]);

  const clarificationInfo = useMemo(() => {
    let totalCount = 0;
    let unresolvedCount = 0;

    questions.forEach(q => {
      const clarifications: Clarification[] =
        clarificationsByQuestion[q.slug] || [];
      totalCount += clarifications.length;
      unresolvedCount += clarifications.filter(c => !c.resolved).length;
    });

    return {
      hasClarifications: totalCount > 0,
      unresolvedCount,
      totalClarificationCount: totalCount,
    };
  }, [questions, clarificationsByQuestion]);

  return {
    status,
    ...clarificationInfo,
  };
};
